import PropTypes from "prop-types";
import { apiErrorMessage } from "../../services/apiError";
import { formatInstant } from "./lib/format";
import { formatCents, formatUsd, moneyMoved } from "./lib/money";

/** "moves about $1,100.00: buys ≈ $600.00, sells ≈ $500.00." — a side with nothing says so. */
function movesText({ total, buys, sells }) {
  if (total === 0n) return "moves no money between holdings.";
  const sides = [
    buys > 0n ? `buys ≈ ${formatCents(buys)}` : null,
    sells > 0n ? `sells ≈ ${formatCents(sells)}` : null,
  ].filter(Boolean);
  if (buys === 0n) sides.push("no buys");
  if (sells === 0n) sides.push("no sells");
  return `moves about ${formatCents(total)}: ${sides.join(", ")}.`;
}

/**
 * Roughly how much money a target change moves at the latest snapshot
 * (walkthrough 8, spec §5.4). PR 4's API has no such figure, so it's worked
 * out here — each changed holding's weight change × the snapshot's total
 * value, in whole cents (lib/money.js) — and the page says how, and that a
 * sync's orders can differ. Without a snapshot there's no figure, and it
 * says why.
 *
 * `latest` is useLatestSnapshot's state; `changes` the diff's holdings.
 */
function MoneyMoved({ changes, latest }) {
  let body;
  if (latest.error) {
    body = (
      <p className="m-0">
        No dollar figure: the latest snapshot couldn&apos;t be read ({apiErrorMessage(latest.error, "it didn't load")}).
      </p>
    );
  } else if (latest.loading && !latest.snapshot) {
    body = <p className="m-0 text-muted-foreground">Working out the money it moves…</p>;
  } else if (!latest.snapshot) {
    body = (
      <p className="m-0">
        No dollar figure yet: the account&apos;s value is first read when the executor records a snapshot. Weights
        are % of the whole account.
      </p>
    );
  } else {
    const { snapshot } = latest;
    const moved = moneyMoved(changes, snapshot.total_value);
    body = moved && (
      <>
        <p className="m-0">
          At the latest snapshot ({formatInstant(snapshot.as_of)} — the account was {formatUsd(snapshot.total_value)}),
          this change {movesText(moved)}
        </p>
        <p className="m-0 text-xs text-muted-foreground">
          Each holding&apos;s weight change × the account&apos;s value then. The sync plan trades from the
          account&apos;s actual positions, so its orders can differ: a holding within the drift band is skipped,
          and prices move.
        </p>
      </>
    );
  }
  return (
    <div data-testid="money-moved" className="flex flex-col gap-1 text-sm">
      {body}
    </div>
  );
}

MoneyMoved.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      weight_before: PropTypes.string.isRequired,
      weight_after: PropTypes.string.isRequired,
    }),
  ).isRequired,
  latest: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    snapshot: PropTypes.shape({ as_of: PropTypes.string, total_value: PropTypes.string }),
    error: PropTypes.object,
  }).isRequired,
};

export default MoneyMoved;
